/**
 * 画面の骨組み(設計書 §5-1)。サイドバー 208px・トップバー 56px・本文。業務ロジックは持たない
 * (どのメニューを出すかは呼び出し側が決めて nav に渡す)。
 *  - PC: サイドバーの表示/非表示をトップバーのボタンで切り替え、次回も同じ状態で開く
 *  - スマホ(≤900px): サイドバーはドロワー。画面を移ると閉じる
 */
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { IconMenu } from '@core/components/icons';

export type ShellNavItem = { to: string; label: string };
export type ShellNavSection = { label: string; items: ShellNavItem[] };

export default function AppShell({
  brand,
  nav,
  title,
  user,
  actions,
  footer,
  banner,
  sideHiddenStorageKey,
  children,
}: {
  brand: ReactNode;
  nav: ShellNavSection[];
  /** トップバーの画面名 */
  title: ReactNode;
  /** トップバー右端(利用者名など) */
  user?: ReactNode;
  /** トップバーの利用者名の左に置く操作(「最新の版に更新」など。2026-10-14 追加) */
  actions?: ReactNode;
  /** サイドバー下端(ログアウトなど) */
  footer?: ReactNode;
  /** トップバーの上に出す帯(テスト環境の表示など) */
  banner?: ReactNode;
  /** サイドバーの表示/非表示を覚えておく localStorage のキー */
  sideHiddenStorageKey?: string;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sideHidden, setSideHidden] = useState(() => {
    if (!sideHiddenStorageKey) return false;
    try {
      return localStorage.getItem(sideHiddenStorageKey) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const toggleSide = () =>
    setSideHidden((v) => {
      if (sideHiddenStorageKey) {
        try {
          localStorage.setItem(sideHiddenStorageKey, v ? '0' : '1');
        } catch {
          /* 保存できなくても表示は切り替える */
        }
      }
      return !v;
    });

  return (
    <div className={`app${menuOpen ? ' menu-open' : ''}${sideHidden ? ' side-hidden' : ''}`}>
      <aside aria-label="メニュー">
        <div className="side-brand">{brand}</div>
        <nav>
          {nav
            .filter((s) => s.items.length > 0)
            .map((s) => (
              <div key={s.label} className="side-group">
                <div className="side-sec">{s.label}</div>
                {s.items.map((i) => (
                  <NavLink key={i.to} to={i.to} className={({ isActive }) => `side-item${isActive ? ' on' : ''}`}>
                    {i.label}
                  </NavLink>
                ))}
              </div>
            ))}
        </nav>
        {footer && <div className="side-foot">{footer}</div>}
      </aside>
      <div className="side-overlay" onClick={() => setMenuOpen(false)} />

      <main>
        {banner}
        <header className="topbar">
          <button className="menu-btn" aria-label="メニューを開く" onClick={() => setMenuOpen(true)}>
            <IconMenu size={18} />
          </button>
          <button className="side-toggle" aria-label="メニューバーの表示/非表示" title="メニューバーの表示/非表示" aria-pressed={!sideHidden} onClick={toggleSide}>
            <IconMenu size={18} />
          </button>
          <h2>{title}</h2>
          {actions}
          {user && <span className="user">{user}</span>}
        </header>
        <div className="content">{children}</div>
      </main>
    </div>
  );
}
